import React, { useCallback, useEffect, useState } from "react"
import { ToastContainer, toast } from "react-toastify"
import { request } from "../../services/request"
import { LogItem, TermsLogsResponse } from "../../services/authinterface"
import Table from "../../components/common/Table"
import { useDebounce } from "../../hooks/useDebounce"
import { useDispatch } from "react-redux"
import { changeloaderState } from "../../store/loaderSlice"
import { AppDispatch } from "../../store/store"
import PrimaryButton from "../../components/subComponents/PrimaryButton"
import { donwloadCSV } from "../../utils/exportFunctions"
import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"
import { Link } from "react-router-dom"

const TermsLogs: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>()
  const [termLogs, setTermLogs] = useState<LogItem[]>([])
  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [totalItems, setTotalItems] = useState(100)
  const [search, setSearch] = useState("")
  const debounceText = useDebounce(search, 500)
  const [sort, setSort] = useState("DESC")
  const [sortCol, setSortCol] = useState("createdAt")

  const fetchLogs = useCallback(async () => {
    try {
      dispatch(changeloaderState(true))
      const params = {
        page: page,
        limit: rowsPerPage,
        search: debounceText,
        sort: sort,
        sortCol: sortCol,
      }
      const response = await request<TermsLogsResponse>({
        url: "/user/getAcceptedTerms",
        method: "POST",
        data: params,
      })

      if (response.success) {
        setTermLogs(response.data)
        setTotalItems(response?.total)
      } else {
        toast.error("Failed to fetch Term Logs: " + response.message)
      }
    } catch (error: any) {
      toast.error(`Error: ${error.message}`)
    } finally {
      dispatch(changeloaderState(false))
    }
  }, [dispatch, page, rowsPerPage, debounceText, sort, sortCol])

  useEffect(() => {
    fetchLogs()
  }, [page, rowsPerPage, debounceText, sort, fetchLogs])

  useEffect(() => {
    if (debounceText) {
      setPage(1)
    }
  }, [debounceText])

  const columns = [
    // {
    //   name: "User Name",
    //   value: (log: LogItem) =>
    //     !log?.user?.first_name && !log.user?.last_name
    //       ? "-"
    //       : (log?.user?.first_name ? log?.user?.first_name : "") +
    //       " " +
    //       (log?.user?.last_name ? log?.user?.last_name : ""),

    //   id: "first_name",
    // },
    {
      name: "User Name",
      value: (log: LogItem) => (
        !log?.user?.first_name && !log?.user?.last_name
          ? "-"
          : (
            <Link className="device-link" to={`/customer/view/${log?.userAccountId}`}>
              {(log?.user?.first_name || "") +
                " " +
                (log?.user?.last_name || "")}
            </Link>
          )
      ),
      id: "name",
      className: "long-text-devices"
    },
    {
      name: "Email",
      value: (log: LogItem) => log?.user?.email,
      id: "email",
      className: "long-text-devices"
    },
    {
      name: "Account Name",
      value: (log: LogItem) =>
        !log?.userAccount?.first_name && !log?.userAccount?.last_name
          ? "-"
          : (log?.userAccount?.first_name ? log?.userAccount?.first_name : "") +
          " " +
          (log?.userAccount?.last_name ? log?.userAccount?.last_name : ""),

      id: "first_name",
      className: "long-text-devices"
    },
    {
      name: "Role",
      value: (log: LogItem) =>
        log?.userAccount?.role === "0"
          ? "Owner"
          : log?.userAccount?.role === "1"
            ? "Admin"
            : log.userAccount.role === "2"
              ? "Viewer"
              : "Kid",
      id: "role",
    },
    {
      name: "Type",
      value: (log: LogItem) => log?.type,
      id: "type",
      // className: "long-text-devices"
    },
    {
      name: "Owner Name",
      value: (log: LogItem) =>
        !log?.userAccount?.owner?.first_name && !log?.userAccount?.owner?.last_name
          ? "-"
          : (log?.userAccount?.owner?.first_name ? log?.userAccount?.owner?.first_name : "") +
          " " +
          (log?.userAccount?.owner?.last_name ? log?.userAccount?.owner?.last_name : ""),

      id: "first_name",
      className: "long-text-devices"
    },
    {
      name: "Owner Email",
      value: (log: LogItem) => log?.userAccount?.owner?.email,
      id: "ownerEmail",
      className: "long-text-devices"
    },
    {
      name: "Version",
      value: (log: LogItem) => (
        <a href={log?.termsFileName} target='_blank' rel='noopener noreferrer'>
          View
        </a>
      ),
      id: "termsFileName",
      className: "long-text-devices"
    },
    {
      name: "Date Accepted",
      value: (log: LogItem) =>
        new Date(log.createdAt).toLocaleString("en-US", {
          timeZone: "America/New_York",
          year: "numeric",
          month: "short",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        }),
      id: "ownerEmail",
      // className: "long-text-devices"
    },
  ]

  const fetchAllLogs = async () => {
    try {
      const params = {
        page: 1,
        search: debounceText,
        limit: 100000,
        sort: sort,
        sortCol: sortCol,
      };
      const response = await request<TermsLogsResponse>({
        url: "/user/getAcceptedTerms",
        method: "POST",
        data: params,
      });
      if (response.success) {
        return response.data;
      } else {
        toast.error("Failed to fetch all logs: " + response.message);
        return [];
      }
    } catch (error: any) {
      toast.error(`Error fetching all logs: ${error.message}`);
      return [];
    }
  };

  const exportCSV = async () => {
    const allData = await fetchAllLogs();
    if (!allData.length) {
      toast.error("No data available to export.");
      return;
    }
    const headers = [
      "Sr No.",
      "User Name",
      "Email",
      "Account Name",
      "Role",
      "Type",
      "Owner Name",
      "Owner Email",
      "Date Accepted",
    ];
    const rows = allData.map((log, index) => [
      index + 1,
      !log?.user?.first_name && !log.user?.last_name
        ? "-"
        : `${log?.user?.first_name || ""} ${log?.user?.last_name || ""}`,
      log?.user?.email,
      !log?.userAccount?.first_name && !log?.userAccount?.last_name
        ? "-"
        : `${log?.userAccount?.first_name || ""} ${log?.userAccount?.last_name || ""}`,
      log?.userAccount?.role === "0"
        ? "Owner"
        : log?.userAccount?.role === "1"
          ? "Admin"
          : log.userAccount.role === "2"
            ? "Viewer"
            : "Kid",
      log?.type,
      !log?.userAccount?.owner?.first_name && !log?.userAccount?.owner?.last_name
        ? "-"
        : `${log?.userAccount?.owner?.first_name || ""} ${log?.userAccount?.owner?.last_name || ""}`,
      log?.userAccount?.owner?.email,
      new Date(log.createdAt).toLocaleString("en-US", {
        timeZone: "America/New_York",
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      }),
    ]);
    donwloadCSV(headers, rows, "TermsLogs.csv");
  };

  const exportPDF = async () => {
    const allData = await fetchAllLogs();
    if (!allData.length) {
      toast.error("No data available to export.");
      return;
    }
    const headers = [
      "Sr No.",
      "User Name",
      "Email",
      "Account Name",
      "Role",
      "Type",
      "Owner Name",
      "Owner Email",
      "Date Accepted",
    ];
    const rows = allData.map((log, index) => [
      index + 1,
      !log?.user?.first_name && !log.user?.last_name
        ? "-"
        : `${log?.user?.first_name || ""} ${log?.user?.last_name || ""}`,
      log?.user?.email,
      !log?.userAccount?.first_name && !log?.userAccount?.last_name
        ? "-"
        : `${log?.userAccount?.first_name || ""} ${log?.userAccount?.last_name || ""}`,
      log?.userAccount?.role === "0"
        ? "Owner"
        : log?.userAccount?.role === "1"
          ? "Admin"
          : log.userAccount.role === "2"
            ? "Viewer"
            : "Kid",
      log?.type,
      !log?.userAccount?.owner?.first_name && !log?.userAccount?.owner?.last_name
        ? "-"
        : `${log?.userAccount?.owner?.first_name || ""} ${log?.userAccount?.owner?.last_name || ""}`,
      log?.userAccount?.owner?.email,
      new Date(log.createdAt).toLocaleString("en-US", {
        timeZone: "America/New_York",
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      }),
    ]);
    const doc = new jsPDF()

    // Title
    doc.setFontSize(16)
    doc.text("Terms-Logs", 14, 15)

    // Table
    autoTable(doc, {
      head: [headers],
      body: rows,
      startY: 25,
      styles: {
        fontSize: 10,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
      },
      columnStyles: {
        0: { cellWidth: "auto" },
        1: { cellWidth: "auto" },
        2: { cellWidth: 30 },
        3: { cellWidth: "auto" },
        4: { cellWidth: "auto" },
        5: { cellWidth: "auto" },
        6: { cellWidth: "auto" },
        7: { cellWidth: 30 },
        8: { cellWidth: 20 },
      },
    });

    doc.save("Terms-Logs")
  };

  return (
    <section className=' dashboard-section dashboard-item'>
      <ToastContainer position='top-right' autoClose={2000} theme='light' />

      <div className='container'>
        <div className='row'>
          <div className='col-12'>
            <div className='dashboard-table-title-section'>
              <h2>User Terms Acceptance Logs</h2>
              <div className='dashboard-table-title-right-side d-flex gap-2'>
                <PrimaryButton
                  label='Export CSV'
                  className='max-w-max-content'
                  onClick={exportCSV}
                />
                <PrimaryButton
                  label='Export PDF'
                  className='max-w-max-content'
                  onClick={exportPDF}
                />
              </div>
            </div>
          </div>
          <div className='col-12 mt-4'>
            <div className='table-container-main'>
              <div className='table-details-section'></div>
              <Table
                rowData={termLogs}
                columns={columns}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                totalItems={totalItems}
                setSortColumn={setSortCol}
                setSortOrder={setSort}
                sortColumn={sortCol}
                sortOrder={sort}
                isSearch
                search={search}
                setSearch={setSearch}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default TermsLogs
